import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AppConfiguration } from "@app/lib/services/app-config.service";
import { BaseHttpService } from "@app/lib/services/base-http.service";
import { IResponse } from "@app/lib/models/response.model";
import { ICaseType } from "../models/case-status.model";
import { ICourtDivisionParam, ICourtUnitType } from "../models/case-assignment.model";
import { Observable } from "rxjs";
import { DocumentRequestModel, DocumentResponseModel, DocumentTypeResponse, GroupPartyResponse, IDTypeResponse, ModDocumentData, LowerCourtParams, LowerCourtResponse, PartyModelRequest, PartyRequest, PartyResponse, PersonResponseModel, MandatoryDocument, checklistDocResponse, BacklogCaseNumberResponse, Party } from "../models/document-details.model";
import { modifyDocPayLoad } from "../models/document-reception.model";
import { TAny } from "@app/lib/models/common.type";
import { query } from "@angular/animations";

@Injectable({
    providedIn: 'root'
})
export class DocumentDetailsService extends BaseHttpService {
    private baseUrl: string;
    constructor(
        http: HttpClient,
        appConfig: AppConfiguration,
    ) {
        super(http, appConfig);
        this.baseUrl = this.appConfig.hostUrl;
    }

    getCourtsByDivision(params: ICourtDivisionParam) {
        return this.get<IResponse<ICourtUnitType[]>>('reports/common/division.htm', { params: { ...params } });
    }

    getDivisions() {
        return this.get<IResponse<ICourtUnitType[]>>('documents-reception/case/court-units/list.htm');
    }


    getCaseTypes() {
        return this.get<IResponse<ICaseType[]>>('case-types/search.htm');
    }

    getDocumentTypes(caseTypeId: number, caseCategory: string): Observable<DocumentTypeResponse> {
        const url = `${this.baseUrl}/document-types/by/case-type.htm?caseTypeId=${caseTypeId}&caseCategory=${caseCategory}`;
        return this.http.get<DocumentTypeResponse>(url);
    }
    getDocumentTypesAdd(queryParams: TAny): Observable<DocumentTypeResponse> {
        const url = `${this.baseUrl}documents-reception/case/doc-types.htm?caseId=${queryParams.caseId}&parentDocId=${queryParams.parentDocId}&appealTypeId=${queryParams.appealTypeId}&caseNumber=${queryParams.caseNumber}`;
        return this.http.get<DocumentTypeResponse>(url);
    }

    getChecklistDoc(queryParams: TAny): Observable<checklistDocResponse> {
        const url = `${this.baseUrl}document-types/by/parent-docid.htm?documentTypeId=${queryParams.documentTypeId}&caseCategory=${queryParams.caseCategory}&appealTypeId=${queryParams.appealTypeId}&caseNumber=${queryParams.caseNumber}`;
        return this.http.get<checklistDocResponse>(url);
    }
    getCourtMaster(): Observable<TAny> {
        const url = `${this.baseUrl}all-courts-master/searchAll.htm`;
        return this.http.get<TAny>(url);
    }
    addCourtMaster(court: TAny): Observable<TAny> {
        const url = `${this.baseUrl}all-courts-master/save.htm`;
        return this.http.post<TAny>(url, court);
    }

    getDivisionMaster(): Observable<TAny> {
        const url = `${this.baseUrl}all-divisions-master/searchAll.htm`;
        return this.http.get<TAny>(url);
    }
    addDivisionMaster(division: TAny): Observable<TAny> {
        const url = `${this.baseUrl}all-divisions-master/save.htm`;
        return this.http.post<TAny>(url, division);
    }
    addJustice(justiceData: TAny): Observable<TAny> {
        const url = `${this.baseUrl}justice-master/save.htm`;
        return this.http.post<TAny>(url, justiceData);
    }
    getJusticeMaster(query: String): Observable<TAny> {
        const url = `${this.baseUrl}justice-master/search.htm?q=${query}`;
        return this.http.get<TAny>(url);
    }

    // for supreme court Document Types
    getSupremeCourtDocumentTypes(queryParams: TAny): Observable<DocumentTypeResponse> {
        const url = `${this.baseUrl}documents-reception/case/doc-types.htm?appealTypeId=${queryParams}`;
        return this.http.get<DocumentTypeResponse>(url);
    }

    getCaseStages(documentTypeId: number): Observable<DocumentTypeResponse> {
        const url = `${this.baseUrl}/documents-reception/case/casephase-types/list.htm?docTypeId=${documentTypeId}`;
        return this.http.get<DocumentTypeResponse>(url);
    }

    fetchParties(partyData: PartyRequest): Observable<PartyResponse> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = `${this.baseUrl}/parties/by/id.htm`;
        return this.http.post<PartyResponse>(url, partyData, { headers });
    }

    // getGroupParty(partyData: any): Observable<PartyResponse> {
    //     const url = `${this.baseUrl}parties/group/govt-party.htm`;
    //     return this.http.post<PartyResponse>(url, partyData);
    // }

    // fetch ID Types
    getIDTypes(): Observable<IDTypeResponse> {
        const url = `${this.baseUrl}id-types/search.htm`;
        return this.http.get<IDTypeResponse>(url);
    }
    getCaseTypesByCutId(courtId: number): Observable<IResponse<ICaseType[]>> {
        const url = `${this.baseUrl}case-types/by/court-unit-type.htm`;
        return this.http.get<IResponse<ICaseType[]>>(url, { params: { courtUnitId: courtId } });
    }

    // assign Group Party
    assignGroupParty(partyData: PartyModelRequest): Observable<GroupPartyResponse> {
        const url = `${this.baseUrl}parties/assign/group.htm`;
        return this.http.post<GroupPartyResponse>(url, partyData);
    }

    // assign Non Group Party
    upsertParty(partyModel: PartyModelRequest): Observable<PersonResponseModel> {
        const url = `${this.baseUrl}parties/upsert.htm`;
        return this.http.post<PersonResponseModel>(url, partyModel);
    }
    // save
    upsertDocument(requestPayload: DocumentRequestModel): Observable<DocumentResponseModel> {
        const url = `${this.baseUrl}documents-reception/documents/upsert.htm`;
        return this.http.post<DocumentResponseModel>(url, requestPayload);
    }

    // fetch Lower Court
    getLowerCourts(params: LowerCourtParams): Observable<LowerCourtResponse> {
        const url = `${this.baseUrl}documents-reception/case/lower-courts.htm`;
        const httpParams = new HttpParams()
            .set('page', params.page.toString())
            .set('start', params.start.toString())
            .set('limit', params.limit.toString())
            .set('property', params.property)
            .set('direction', params.direction);

        return this.http.get<LowerCourtResponse>(url, { params: httpParams });
    }
    getBacklogCaseNumberExist(caseNumber: String): Observable<BacklogCaseNumberResponse> {
        const url = `${this.baseUrl}case-phase/backlog-case.htm?caseNumber=${caseNumber}`;
        return this.http.get<BacklogCaseNumberResponse>(url);
    }
}   